import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { makeExecutableSchema } from '@graphql-tools/schema';

import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';
import { Logger } from '@e2m-customer-portal/hooks';
import { withScalars } from 'apollo-link-scalars';
import { parseISO, toDate } from 'date-fns';
import { GraphQLScalarType, Kind } from 'graphql';
import { typeDefs as scalarTypeDefs } from 'graphql-scalars';
import { FieldValue } from '../../generated/graphql';
import typeDefs from '../../schema';
import { fetchWithRequestId, getRequestId } from '../../utils';

const schema = makeExecutableSchema({ typeDefs: [...scalarTypeDefs, typeDefs] });

const typesMap = {
  DateTime: new GraphQLScalarType({
    name: 'DateTime',
    serialize: (parsed: unknown) => {
      if (parsed instanceof Date || typeof parsed === 'number') {
        return (typeof parsed === 'number' ? toDate(parsed) : parsed).toISOString();
      } else if (parsed == null) {
        return parsed;
      }
      throw new Error('Logic error');
    },
    parseValue: (raw: unknown) => typeof raw === 'string' && parseISO(raw),
    parseLiteral: (ast) => {
      if (ast.kind === Kind.STRING) {
        return parseISO(ast.value);
      }
      return null;
    },
  }),
};

const httpOptions = {
  uri: '/graphql',
  credentials: 'include',
  batchMax: 100,
};

export function createApolloClient(fetchImplementation: typeof fetch, logger: Logger): ApolloClient<unknown> {
  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // "marketLocations" is paginated, this merges the pages into a single list
            // see also: https://www.apollographql.com/docs/react/pagination/cursor-based
            marketLocations: relayStylePagination(),
          },
        },
        ForecastSlice: {
          fields: {
            fields: {
              merge(existing: Array<FieldValue>, incoming: Array<FieldValue>, { readField, mergeObjects }) {
                const merged = existing ? existing.slice() : [];
                const fieldValueTypeToIndex: Record<string, number> = {};
                if (existing) {
                  existing.forEach((fieldValue, index) => {
                    fieldValueTypeToIndex[readField<string>('type', fieldValue)!] = index;
                  });
                }
                incoming.forEach((fieldValue) => {
                  const type = readField<string>('type', fieldValue)!;
                  const index = fieldValueTypeToIndex[type];
                  const completeFieldValue = {
                    ...fieldValue,
                    value: { value: fieldValue.watt, unit: 'W' },
                  };
                  if (typeof index === 'number') {
                    // Merge existing and new field value
                    merged[index] = mergeObjects(merged[index], completeFieldValue);
                  } else {
                    // Never seen this field value before
                    fieldValueTypeToIndex[type] = merged.length;
                    merged.push(completeFieldValue);
                  }
                });
                return merged;
              },
            },
          },
        },
        FieldValue: {
          fields: {
            value(_, { readField }) {
              return { value: readField('watt'), unit: 'W' };
            },
          },
        },
      },
    }),
    link: ApolloLink.from([
      withScalars({ schema, typesMap }),
      onError(({ operation, graphQLErrors, networkError }) => {
        const { response } = operation.getContext();

        if (graphQLErrors) {
          graphQLErrors.forEach((error) => {
            Object.assign(error, { requestId: getRequestId(response) });
            logger.error(`[GraphQL error]: Message: ${error.message}`, { error });
          });
        }

        if (networkError) {
          Object.assign(networkError, { requestId: getRequestId(response) });
          logger.error(`[Network error]: ${networkError.message}`, { error: networkError });
        }
      }),
      new BatchHttpLink({
        ...httpOptions,
        fetch: fetchWithRequestId(fetchImplementation),
      }),
    ]),
  });
}
